import React, { useRef, useState } from 'react';
import type { FormDefinition, FormAnswer, FormResponsePlaintext } from '../../types/forms';
import { PhoneNumberInput } from '../PhoneNumberInput';
import { formatFullPhone, isValidLocalPhone } from '../../utils/phoneFormat';
import { FormField } from './FormField';

interface FormRendererProps {
  form: FormDefinition;
  /** Pre-populated contact info (e.g. from booking flow). Hides contact fields when present. */
  contactInfo?: { firstName?: string; lastName?: string; phone?: string; countryCode?: string };
  /** Suppress contact fields even when the form collects contact info (e.g. token identifies respondent). */
  hideContactFields?: boolean;
  submitLabel?: string;
  onSubmit: (data: FormResponsePlaintext) => void | Promise<void>;
  submitting?: boolean;
}

function isAnswerEmpty(answer: FormAnswer | undefined): boolean {
  if (!answer) return true;
  if (answer.type === 'text') return !answer.value.trim();
  if (answer.type === 'options') return answer.values.length === 0;
  return true;
}

function validateEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function validatePhoneDigits(digits: string) {
  return isValidLocalPhone(digits);
}

export function FormRenderer({
  form,
  contactInfo,
  hideContactFields = false,
  submitLabel = 'Submit',
  onSubmit,
  submitting = false,
}: FormRendererProps) {
  const [answers, setAnswers] = useState<Record<string, FormAnswer>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [firstName, setFirstName] = useState(contactInfo?.firstName ?? '');
  const [lastName, setLastName] = useState(contactInfo?.lastName ?? '');
  const [phone, setPhone] = useState(contactInfo?.phone ?? '');
  const [countryCode, setCountryCode] = useState(contactInfo?.countryCode ?? 'US');

  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const sortedFields = [...form.fields].sort((a, b) => a.position - b.position);
  const collectContactInfo = form.collectContactInfo ?? false;
  const contactInfoRequired = form.contactInfoRequired ?? false;
  const hasPrefilledContact = Boolean(
    contactInfo?.firstName?.trim() && contactInfo?.lastName?.trim() && contactInfo?.phone?.trim(),
  );
  const showContactFields = collectContactInfo && !hideContactFields && !hasPrefilledContact;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (showContactFields && contactInfoRequired) {
      if (!firstName.trim()) newErrors._firstName = 'First name is required';
      if (!lastName.trim()) newErrors._lastName = 'Last name is required';
      if (!phone.trim()) {
        newErrors._phone = 'Phone number is required';
      } else if (!validatePhoneDigits(phone)) {
        newErrors._phone = 'Please enter a 10-digit phone number';
      }
    } else if (showContactFields && phone.trim() && !validatePhoneDigits(phone)) {
      newErrors._phone = 'Please enter a 10-digit phone number';
    }

    for (const field of sortedFields) {
      const answer = answers[field.id];

      if (field.required && isAnswerEmpty(answer)) {
        if (field.type === 'consent') {
          newErrors[field.id] = 'You must agree to continue';
        } else if (field.type === 'signature') {
          newErrors[field.id] = 'Signature is required';
        } else if (field.type === 'rating') {
          newErrors[field.id] = 'Please select a rating';
        } else if (field.type === 'linear_scale') {
          newErrors[field.id] = 'Please select a value';
        } else if (field.type === 'body_map') {
          newErrors[field.id] = 'Please mark at least one area';
        } else {
          newErrors[field.id] = `${field.label} is required`;
        }
        continue;
      }

      if (answer?.type === 'text' && answer.value.trim()) {
        if (field.textFormat === 'email' && !validateEmail(answer.value)) {
          newErrors[field.id] = 'Please enter a valid email address';
        }
        if (field.textFormat === 'phone') {
          const localDigits = answer.value.replace(/[^\d]/g, '').slice(-10);
          if (!validatePhoneDigits(localDigits)) {
            newErrors[field.id] = 'Please enter a 10-digit phone number';
          }
        }
      }

      if (field.type === 'consent' && field.required) {
        if (!answer || answer.type !== 'text' || answer.value !== 'true') {
          newErrors[field.id] = 'You must agree to continue';
        }
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstKey = Object.keys(newErrors)[0];
      const el = fieldRefs.current[firstKey];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }

    return true;
  };

  const buildContactInfo = () => {
    if (!collectContactInfo) return undefined;

    if (showContactFields) {
      const trimmedFirst = firstName.trim();
      const trimmedLast = lastName.trim();
      const trimmedPhone = phone.trim();
      if (!trimmedFirst && !trimmedLast && !trimmedPhone) return undefined;
      return {
        firstName: trimmedFirst,
        lastName: trimmedLast,
        phone: trimmedPhone ? formatFullPhone(countryCode, trimmedPhone) : '',
      };
    }

    if (hasPrefilledContact) {
      return {
        firstName: contactInfo!.firstName!.trim(),
        lastName: contactInfo!.lastName!.trim(),
        phone: formatFullPhone(contactInfo!.countryCode ?? 'US', contactInfo!.phone!.trim()),
      };
    }

    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const response: FormResponsePlaintext = { answers };
    const contact = buildContactInfo();
    if (contact) {
      response.contactInfo = contact;
    }

    await onSubmit(response);
  };

  const setRef = (id: string) => (el: HTMLDivElement | null) => {
    fieldRefs.current[id] = el;
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <h2 className="text-xl font-bold text-site-text mb-6">{form.name}</h2>

      <div className="space-y-5">
        {showContactFields && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div ref={setRef('_firstName')}>
                <label className="block text-sm font-medium text-site-text mb-2">
                  First Name
                  {contactInfoRequired && <span className="text-site-danger"> *</span>}
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={e => {
                    setFirstName(e.target.value);
                    if (errors._firstName) setErrors(prev => { const n = { ...prev }; delete n._firstName; return n; });
                  }}
                  placeholder="Jane"
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none ${
                    errors._firstName ? 'border-site-danger/40 focus:border-site-danger' : 'border-site-border'
                  }`}
                />
                {errors._firstName && <p className="text-site-danger text-sm mt-1">{errors._firstName}</p>}
              </div>

              <div ref={setRef('_lastName')}>
                <label className="block text-sm font-medium text-site-text mb-2">
                  Last Name
                  {contactInfoRequired && <span className="text-site-danger"> *</span>}
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={e => {
                    setLastName(e.target.value);
                    if (errors._lastName) setErrors(prev => { const n = { ...prev }; delete n._lastName; return n; });
                  }}
                  placeholder="Smith"
                  className={`w-full px-4 py-3 rounded-xl border-2 transition-colors focus:outline-none ${
                    errors._lastName ? 'border-site-danger/40 focus:border-site-danger' : 'border-site-border'
                  }`}
                />
                {errors._lastName && <p className="text-site-danger text-sm mt-1">{errors._lastName}</p>}
              </div>
            </div>

            <div ref={setRef('_phone')}>
              <label htmlFor="phone" className="block text-sm font-medium text-site-text mb-2">
                Phone Number
                {contactInfoRequired && <span className="text-site-danger"> *</span>}
              </label>
              <PhoneNumberInput
                countryCode={countryCode}
                phone={phone}
                onCountryCodeChange={code => {
                  setCountryCode(code);
                  if (errors._phone) setErrors(prev => { const n = { ...prev }; delete n._phone; return n; });
                }}
                onPhoneChange={digits => {
                  setPhone(digits);
                  if (errors._phone) setErrors(prev => { const n = { ...prev }; delete n._phone; return n; });
                }}
                error={errors._phone}
              />
            </div>
          </>
        )}

        {sortedFields.map(field => (
          <div key={field.id} ref={setRef(field.id)}>
            <FormField
              field={field}
              value={answers[field.id]}
              onChange={answer => {
                setAnswers(prev => ({ ...prev, [field.id]: answer }));
                if (errors[field.id]) {
                  setErrors(prev => { const n = { ...prev }; delete n[field.id]; return n; });
                }
              }}
              error={errors[field.id]}
            />
          </div>
        ))}
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="mt-8 w-full bg-site-primary text-site-on-primary border border-site-primary-outline font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60"
      >
        {submitting ? 'Submitting…' : submitLabel}
      </button>
    </form>
  );
}
